import { Machine, send, assign, spawn } from "xstate";
import { ClockWorkModule } from "@/store/modules/clockWork.module";
import { ClockWorkEntryModel } from "@/store/models/clockWork/clockWorkEntry";
import { toaster } from "@/utilities/toaster";
import projectTaskPickerMachine from "@/router/views/clockWork/machines/projectTaskPickerMachine";
import { ProjectModule } from "@/store/modules/project.module";
function getFormattedDateString(value) {
    return `${value.getFullYear()}-${value.getMonth() + 1}-${value.getDate()}T00:00:00`;
}
function checkRunningEntries() {
    if (ClockWorkModule.clockWorkNullEntries.length > 0) {
        return ClockWorkModule.clockWorkNullEntries.some((x) => x.clockWorkEntry.endTimeTzFormatted == "now");
    }
    else {
        return false;
    }
}
function checkRunningEntriesNotAvailable() {
    if (ClockWorkModule.clockWorkNullEntries.length > 0) {
        return ClockWorkModule.clockWorkNullEntries.every((x) => x.clockWorkEntry.endTimeTzFormatted != "now");
    }
    else {
        return true;
    }
}
const clockWorkMachine = Machine({
    id: "clockWork",
    context: {
        operationDate: undefined,
        displayDate: undefined,
        checkedEntries: [],
        associationMachine: null,
    },
    initial: "idle",
    states: {
        idle: {
            always: { target: "checkingLongRunningEntries" },
        },
        checkingLongRunningEntries: {
            always: [
                {
                    target: "longRunningEntry",
                    cond: "checkRunningEntries",
                },
                {
                    target: "clockWorkOpertations",
                    cond: "checkRunningEntriesNotAvailable",
                },
            ],
        },
        longRunningEntry: {
            id: "longRunningEntry",
            tags: "long-running-entry",
            initial: "entryNeedsFix",
            states: {
                entryNeedsFix: {
                    tags: "entryNeedsFix",
                    on: {
                        FIXING_ENTRY: { target: "fixingLongRunningEntry" },
                        DELETE_RUNNING_ENTRY: { target: "deletingLongRunningEntry" },
                        CROSSOVER: { target: "endLongRunningEntryAndResume" },
                    },
                },
                fixingLongRunningEntry: {
                    tags: ["fix-entry", "entryNeedsFix"],
                    invoke: {
                        id: "savingFixedEntries",
                        src: (context, event) => {
                            return ClockWorkModule.AdjustRunningEntry({ entryId: event.entryId, newStartTime: event.newStartTime, newEndTime: event.newEndTime, isMnco: true });
                        },
                        onDone: [
                            { target: "clearClockWorkNullEntries", cond: "checkRunningEntriesNotAvailable", actions: "longRunning" },
                            { target: "entryNeedsFix", cond: "checkRunningEntries", actions: "longRunning" },
                        ],
                        onError: { target: "entryNeedsFix" },
                    },
                },
                deletingLongRunningEntry: {
                    tags: ["delete-null-entry", "entryNeedsFix"],
                    invoke: {
                        id: "deletingNullEntry",
                        src: (context, event) => {
                            return ClockWorkModule.DeleteRunningEntry(event.deleteId);
                        },
                        onDone: [
                            { target: "clearClockWorkNullEntries", cond: "checkRunningEntriesNotAvailable", actions: "deleteRunningEntryDone" },
                            { target: "entryNeedsFix", cond: "checkRunningEntries", actions: "deleteRunningEntryDone" },
                        ],
                        onError: { target: "entryNeedsFix" },
                    },
                },
                endLongRunningEntryAndResume: {
                    tags: ["end-restarting", "entryNeedsFix"],
                    invoke: {
                        id: "crossover",
                        src: (context, event) => {
                            return ClockWorkModule.CrossoverEntry(event.runningEntry);
                        },
                        onDone: [
                            { target: "clearClockWorkNullEntries", cond: "checkRunningEntriesNotAvailable", actions: "crossoverDone" },
                            { target: "entryNeedsFix", cond: "checkRunningEntries", actions: "crossoverDone" },
                        ],
                        onError: { target: "entryNeedsFix" },
                    },
                },
                clearClockWorkNullEntries: {
                    invoke: {
                        id: "ClearEntries",
                        src: (context, event) => {
                            return ClockWorkModule.clearNullEntryForMNCO();
                        },
                        onDone: { target: "#clockWork.clockWorkOpertations" },
                        onError: { target: "entryNeedsFix" },
                    },
                },
            },
        },
        clockWorkOpertations: {
            type: "parallel",
            states: {
                operations: {
                    id: "operations",
                    initial: "idle",
                    states: {
                        idle: {
                            on: {
                                START: { target: "starting" },
                                RESTART: { target: "starting" },
                                DELETE: { target: "deleting" },
                                RESUME_RUNNING: { target: "running" },
                                SWITCH_WORK_STATUS: { target: "switchingWorkStatus" },
                            },
                        },
                        starting: {
                            entry: [
                                (_, __) => {
                                    console.log("Starting: " + new Date().toISOString().substr(11, 8));
                                },
                                "clearLiveTimer",
                            ],
                            invoke: {
                                id: "liveEntryAddition",
                                //src: 'delayedSuccess',
                                src: (context, event) => {
                                    if (event.type == "RESTART") {
                                        return ClockWorkModule.RestartEntry(event.entry);
                                    }
                                    let liveEntry = new ClockWorkEntryModel();
                                    liveEntry.startTime = new Date();
                                    ClockWorkModule.GetClockWorkRunningEntries();
                                    return ClockWorkModule.AddEntry(liveEntry);
                                },
                                onDone: { target: "running" },
                                onError: { target: "error" },
                            },
                        },
                        running: {
                            entry: (_, __) => {
                                console.log("Running: " + new Date().toISOString().substr(11, 8));
                            },
                            on: {
                                STOP: { target: "stopping" },
                                START: { target: "starting" },
                                RESTART: { target: "starting" },
                                DELETE: { target: "deleting" },
                                SWITCH_WORK_STATUS: { target: "switchingWorkStatus" },
                            },
                        },
                        stopping: {
                            entry: [
                                (_, __) => {
                                    console.log("Stopping: " + new Date().toISOString().substr(11, 8));
                                },
                            ],
                            invoke: {
                                id: "liveEntryStopping",
                                src: (context, event) => {
                                    return ClockWorkModule.EndEntry(event.liveEntry);
                                },
                                onDone: { target: "stopped" },
                                onError: { target: "error" },
                            },
                        },
                        stopped: {
                            entry: (_, __) => {
                                console.log("Stopped: " + new Date().toISOString().substr(11, 8));
                            },
                            on: {
                                START: { target: "starting" },
                                RESTART: { target: "starting" },
                                DELETE: { target: "deleting" },
                                RESUME_RUNNING: { target: "running" },
                                SWITCH_WORK_STATUS: { target: "switchingWorkStatus" },
                            },
                        },
                        deleting: {
                            invoke: {
                                id: "deleteEntries",
                                src: (context, event) => {
                                    return ClockWorkModule.DeleteEntries([event.entryId]);
                                },
                                onDone: { target: "deleted", actions: "deleteEntryDone" },
                                onError: { target: "error" },
                            },
                        },
                        deleted: {
                            entry: (_, __) => {
                                console.log("Deleted: " + new Date().toISOString().substr(11, 8));
                            },
                            on: {
                                START: { target: "starting" },
                                RESTART: { target: "starting" },
                                DELETE: { target: "deleting" },
                                STOP: { target: "stopping" },
                                RESUME_RUNNING: { target: "running" },
                                SWITCH_WORK_STATUS: { target: "switchingWorkStatus" },
                            },
                        },
                        switchingWorkStatus: {
                            invoke: {
                                id: "switchingWorkStatus",
                                src: (context, event) => {
                                    return ClockWorkModule.UpdateWorkStatus({ entries: event.entryId, isWFO: event.isWFO });
                                },
                                onDone: { target: "running" },
                                onError: { target: "error" },
                            },
                        },
                        error: { type: "final" },
                    },
                },
                review: {
                    initial: "idle",
                    states: {
                        idle: {
                            always: [{ target: "dayChanging" }],
                        },
                        dayChanging: {
                            entry: "clearAllEntries",
                            invoke: {
                                id: "fetchDayEntries",
                                src: "fetchDayEntries",
                                onDone: { target: "dayLoaded" },
                                onError: { target: "serviceUnavailable" },
                            },
                        },
                        dayLoaded: {
                            initial: "unknown",
                            on: {
                                ENTRY_TOGGLE: [
                                    {
                                        target: "dayLoaded",
                                        actions: "entryToggle",
                                    },
                                ],
                                CLEAR_ENTRY_SELECTIONS: [
                                    {
                                        target: "dayLoaded",
                                        actions: "clearAllEntries",
                                    },
                                ],
                                GO_TO_TODAY: {
                                    target: "dayChanging",
                                    actions: "setToday",
                                },
                                CHANGE_DISPLAY_DATE: {
                                    target: "dayChanging",
                                    actions: "changeDate",
                                },
                                PREV_DAY: {
                                    target: "dayChanging",
                                    actions: "decrementDay",
                                },
                                NEXT_DAY: {
                                    target: "dayChanging",
                                    actions: "incrementDay",
                                },
                            },
                            states: {
                                unknown: {
                                    always: [
                                        { target: "nullSelection", cond: "hasNoSelection" },
                                        { target: "singleSelection", cond: "hasSingleSelection" },
                                        { target: "multiSelection", cond: "hasMultipleSelections" },
                                    ],
                                },
                                nullSelection: {
                                    entry: "clearAllEntries",
                                },
                                singleSelection: {
                                    tags: ["editable", "deletable", "assignable", "adjustable", "restartable", "splitable"],
                                    initial: "features",
                                    states: {
                                        features: {
                                            on: {
                                                SELECT_PROJECT_TASK: { target: "selectProjectAndTask" },
                                                ADJUST_ENTRY: { target: "adjustEntry" },
                                                CHECKED_ENTRY_DELETE: { target: "deleting" },
                                                SPLIT_ENTRY: { target: "splitEntry" },
                                                PREV_ENTRY: { actions: "prevEntry" },
                                                NEXT_ENTRY: { actions: "nextEntry" },
                                                SWITCH_WORK: { target: "switchingWorkStatus" },
                                                SET_DEFAULT_PROJECT_TASK: { target: "setDefaultProjectAndTask" },
                                            },
                                        },
                                        selectProjectAndTask: {
                                            entry: [
                                                assign({
                                                    associationMachine: (_, __) => spawn(projectTaskPickerMachine, { name: "projectTaskPickerMachine", sync: true }),
                                                }),
                                            ],
                                            initial: "display",
                                            on: {
                                                FEATURES: { target: "features" },
                                                UPDATE_PICKER: {
                                                    actions: send((context, event) => ({ ...event, type: "GOTOTAB" }), { to: (context) => context.associationMachine }),
                                                },
                                                FETCH_TASKS: {
                                                    actions: send((context, event) => ({ ...event, type: event.type }), { to: (context) => context.associationMachine }),
                                                },
                                                CHANGE_PROJECT: {
                                                    actions: send((context, event) => ({ ...event, type: event.type }), { to: (context) => context.associationMachine }),
                                                },
                                                ASSIGN: { target: ".assigningProjectTask" },
                                            },
                                            states: {
                                                display: {
                                                    tags: ["displaying"],
                                                },
                                                assigningProjectTask: {
                                                    invoke: {
                                                        id: "assigningEntriesToProjectTaskCombos",
                                                        //src: "delayedSuccess",
                                                        src: (context, event) => {
                                                            return ClockWorkModule.AssignEntries({ taskId: event.taskId, entries: context.checkedEntries });
                                                        },
                                                        onDone: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.singleSelection.features", actions: "associationsDone" },
                                                        onError: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.singleSelection.selectProjectAndTask" },
                                                    },
                                                },
                                            },
                                            exit: [
                                                assign({
                                                    associationMachine: (ctx, event) => {
                                                        ctx.associationMachine.stop();
                                                        return null;
                                                    },
                                                }),
                                            ],
                                        },
                                        adjustEntry: {
                                            tags: "entry-adjustment",
                                            on: {
                                                FEATURES: { target: "features" },
                                                SAVE_ADJUSTED_ENTRY: { target: "savingAdjustedEntry" },
                                            },
                                        },
                                        savingAdjustedEntry: {
                                            tags: "entry-adjustment",
                                            invoke: {
                                                id: "savingAdjustedEntries",
                                                src: (context, event) => {
                                                    return ClockWorkModule.AdjustEntry({
                                                        entryId: event.entryId,
                                                        newStartTime: event.newStartTime,
                                                        newEndTime: event.newEndTime,
                                                        shouldAppendEntries: true,
                                                    });
                                                },
                                                onDone: { target: "features", actions: "adjustmentsDone" },
                                                onError: { target: "adjustEntry" },
                                            },
                                        },
                                        splitEntry: {
                                            tags: "split-entry",
                                            on: {
                                                FEATURES: { target: "features" },
                                                SAVE_SPLITTED_ENTRY: { target: "savingSplittedEntry" },
                                            },
                                        },
                                        savingSplittedEntry: {
                                            tags: "split-entry",
                                            invoke: {
                                                id: "savingAdjustedEntries",
                                                src: (context, event) => {
                                                    return ClockWorkModule.SplitEntry({ entryId: event.entryId, splitTime: event.splitTime });
                                                },
                                                onDone: { target: "features", actions: "splitEntryDone" },
                                                onError: { target: "splitEntry" },
                                            },
                                        },
                                        deleting: {
                                            invoke: {
                                                id: "deleteEntries",
                                                src: "deleteEntries",
                                                onDone: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.nullSelection", actions: "deleteEntryDone" },
                                                onError: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.nullSelection" },
                                            },
                                        },
                                        switchingWorkStatus: {
                                            tags: "switchingWorkStatus",
                                            invoke: {
                                                id: "switchingWorkStatus",
                                                src: (context, event) => {
                                                    return ClockWorkModule.UpdateWorkStatus({ entries: context.checkedEntries, isWFO: event.isWFO });
                                                },
                                                onDone: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.singleSelection", actions: "workFromHomeOrOfficeDone" },
                                                onError: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.nullSelection" },
                                            },
                                        },
                                        setDefaultProjectAndTask: {
                                            tags: "defaultProjectAndTask",
                                            invoke: {
                                                id: "defaultProjectAndTask",
                                                src: (ctx, event) => {
                                                    return ProjectModule.AddDefaultProjectAndTask(event.clockWorkSettingRequest);
                                                },
                                                onDone: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.singleSelection", actions: "setAsDefaultTaskAndProject" },
                                                onError: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.nullSelection" },
                                            },
                                        },
                                    },
                                },
                                multiSelection: {
                                    tags: ["deletable", "assignable", "iSdefaultProjectTask"],
                                    initial: "features",
                                    states: {
                                        features: {
                                            on: {
                                                SELECT_PROJECT_TASK: { target: "selectProjectAndTask" },
                                                ADJUST_ENTRY: { target: "adjustEntry" },
                                                CHECKED_ENTRY_DELETE: { target: "deleting" },
                                                SWITCH_WORK: { target: "switchingWorkStatus" },
                                            },
                                        },
                                        selectProjectAndTask: {
                                            entry: [
                                                assign({
                                                    associationMachine: (_, __) => spawn(projectTaskPickerMachine, { name: "projectTaskPickerMachine", sync: true }),
                                                }),
                                            ],
                                            initial: "display",
                                            on: {
                                                FEATURES: { target: "features" },
                                                UPDATE_PICKER: {
                                                    actions: send((context, event) => ({ ...event, type: "GOTOTAB" }), { to: (context) => context.associationMachine }),
                                                },
                                                FETCH_TASKS: {
                                                    actions: send((context, event) => ({ ...event, type: event.type }), { to: (context) => context.associationMachine }),
                                                },
                                                CHANGE_PROJECT: {
                                                    actions: send((context, event) => ({ ...event, type: event.type }), { to: (context) => context.associationMachine }),
                                                },
                                                ASSIGN: { target: ".assigningProjectTask" },
                                            },
                                            states: {
                                                display: {
                                                    tags: ["displaying"],
                                                },
                                                assigningProjectTask: {
                                                    invoke: {
                                                        id: "assigningEntriesToProjectTaskCombos",
                                                        //src: "delayedSuccess",
                                                        src: (context, event) => {
                                                            return ClockWorkModule.AssignEntries({ taskId: event.taskId, entries: context.checkedEntries });
                                                        },
                                                        onDone: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.multiSelection.features", actions: "associationsDone" },
                                                        onError: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.multiSelection.selectProjectAndTask" },
                                                    },
                                                },
                                            },
                                            exit: [
                                                assign({
                                                    associationMachine: (ctx, event) => {
                                                        ctx.associationMachine.stop();
                                                        return null;
                                                    },
                                                }),
                                            ],
                                        },
                                        adjustEntry: {
                                            on: {
                                                FEATURES: { target: "features" },
                                            },
                                        },
                                        deleting: {
                                            invoke: {
                                                id: "deleteEntries",
                                                src: "deleteEntries",
                                                onDone: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.nullSelection", actions: "deleteEntryDone" },
                                                onError: { target: "#clockWork.clockWorkOpertations.review.serviceUnavailable" },
                                            },
                                        },
                                        switchingWorkStatus: {
                                            tags: "switchingWorkStatus",
                                            invoke: {
                                                id: "switchingWorkStatus",
                                                src: (context, event) => {
                                                    return ClockWorkModule.UpdateWorkStatus({ entries: context.checkedEntries, isWFO: event.isWFO });
                                                },
                                                onDone: { target: "#clockWork.clockWorkOpertations.review.dayLoaded.multiSelection.features", actions: "workFromHomeOrOfficeDone" },
                                                onError: { target: "#clockWork.clockWorkOpertations.review.serviceUnavailable" },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                        serviceUnavailable: { type: "final" },
                    },
                },
                settings: {
                    initial: "idle",
                    states: {
                        idle: {
                            on: {
                                DEFAULT_SETTING: { target: "gettingProjects" },
                            },
                        },
                        gettingProjects: {
                            invoke: {
                                id: "gettingProjects",
                                src: (_, __) => {
                                    return ProjectModule.GetProjectsForDefaultProject();
                                },
                                onDone: { target: "loadedProject" },
                                onError: { target: "serviceUnavailable" },
                            },
                        },
                        loadedProject: {
                            on: {
                                SELECTING_PROJECT: { target: "gettingSelectedProjectTasks" },
                                SAVE_DEFAULT_PROJECT_TASK: { target: "savingDefaultTask" },
                                CANCEL_DEFAULT_PROJECT_TASK: { target: "idle" },
                            },
                        },
                        gettingSelectedProjectTasks: {
                            invoke: {
                                id: "gettingSelectedProjectTasks",
                                src: (_, __) => {
                                    return ProjectModule.GetTasksForSelection(__.projectId);
                                },
                                onDone: { target: "loadedTask" },
                                onError: { target: "serviceUnavailable" },
                            },
                        },
                        loadedTask: {
                            on: {
                                SELECTING_PROJECT: { target: "gettingSelectedProjectTasks" },
                                SAVE_DEFAULT_PROJECT_TASK: { target: "savingDefaultTask" },
                                CANCEL_DEFAULT_PROJECT_TASK: { target: "idle" },
                            },
                        },
                        savingDefaultTask: {
                            invoke: {
                                id: "savingDefaultTask",
                                src: (_, __) => {
                                    return ProjectModule.AddDefaultProjectAndTask(__.clockWorkSettingRequest);
                                },
                                onDone: { target: "idle", actions: "defaultProjectAndTaskDone" },
                                onError: { target: "serviceUnavailable" },
                            },
                        },
                        serviceUnavailable: { type: "final" },
                    },
                },
                deletingDefaultProjectandTask: {
                    initial: "idle",
                    states: {
                        idle: {
                            on: {
                                REMOVE_DEFAULT_PROJECT_TASK: { target: "deletingDefaults" },
                            },
                        },
                        deletingDefaults: {
                            invoke: {
                                id: "deletingDefaults",
                                src: (_, __) => {
                                    return ProjectModule.RemoveDefaultProjectAndTask();
                                },
                                onDone: { target: "idle" },
                                onError: { target: "serviceUnavailable" },
                            },
                        },
                        serviceUnavailable: { type: "final" },
                    },
                },
                gapFilling: {
                    initial: "idle",
                    states: {
                        idle: {
                            on: {
                                UPFILL: { target: "upFilling" },
                                DOWNFILL: { target: "downFilling" },
                            },
                        },
                        upFilling: {
                            invoke: {
                                id: "upFilling",
                                src: (context, event) => {
                                    return ClockWorkModule.UpFill(event.payload);
                                },
                                onDone: { target: "idle" },
                                onError: { target: "serviceUnavailable" },
                            },
                        },
                        downFilling: {
                            invoke: {
                                id: "downFilling",
                                src: (context, event) => {
                                    return ClockWorkModule.DownFill(event.payload);
                                },
                                onDone: { target: "idle" },
                                onError: { target: "serviceUnavailable" },
                            },
                        },
                        serviceUnavailable: { type: "final" },
                    },
                },
            },
        },
    },
}, {
    actions: {
        changeDate: assign({
            displayDate: (context, event) => event.value,
        }),
        setToday: assign({
            displayDate: (context, event) => context.operationDate,
        }),
        decrementDay: assign({
            displayDate: (context, event) => {
                let dMinus = new Date(context.displayDate);
                dMinus.setDate(context.displayDate.getDate() - 1);
                return dMinus;
            },
        }),
        incrementDay: assign({
            displayDate: (context, event) => {
                if (context.displayDate.toDateString() == context.operationDate.toDateString()) {
                    return context.displayDate;
                }
                let dPlus = new Date(context.displayDate);
                dPlus.setDate(context.displayDate.getDate() + 1);
                return dPlus;
            },
        }),
        nextEntry: assign({
            checkedEntries: (ctx, event) => {
                var checkedEntries = [];
                return checkedEntries.concat(event.nextEntryId);
            },
        }),
        prevEntry: assign({
            checkedEntries: (ctx, event) => {
                var checkedEntries = [];
                return checkedEntries.concat(event.prevEntryId);
            },
        }),
        clearAllEntries: assign({
            checkedEntries: (_, __) => [],
        }),
        entryToggle: assign({
            checkedEntries: (ctx, event) => {
                if (ctx.checkedEntries.includes(event.entryId)) {
                    return ctx.checkedEntries.filter((r) => r !== event.entryId);
                }
                return ctx.checkedEntries.concat(event.entryId);
            },
        }),
        clearLiveTimer: () => {
            ClockWorkModule.ResetLiveTimer();
        },
        associationsDone: () => {
            toaster.ShowSuccess("Project & Task has been associated successfully.");
        },
        adjustmentsDone: () => {
            toaster.ShowSuccess("Adjustements have been made to this entry successfully.");
        },
        longRunning: () => {
            toaster.ShowSuccess("The long running entry has been adjusted.");
        },
        deleteRunningEntryDone: () => {
            toaster.ShowSuccess("Time entry has been deleted successfully.");
        },
        deleteEntryDone: () => {
            toaster.ShowSuccess("The selected time entry / entries have been deleted successfully.");
        },
        crossoverDone: () => {
            toaster.ShowSuccess("Crossover adjustment has been made successfully.");
        },
        defaultProjectAndTaskDone: () => {
            toaster.ShowSuccess("Default Setting has been updated successfully");
        },
        splitEntryDone: () => {
            toaster.ShowSuccess("Time entry has been split successfully.");
        },
        workFromHomeOrOfficeDone: () => {
            toaster.ShowSuccess("WFO / WFH setting for the selected entry / entries has been updated successfully.");
        },
        setAsDefaultTaskAndProject: () => {
            toaster.ShowSuccess("Default Project and Task have been set successfully.");
        },
    },
    guards: {
        hasSingleSelection: (ctx) => ctx.checkedEntries.length === 1,
        hasMultipleSelections: (ctx) => ctx.checkedEntries.length > 1,
        hasNoSelection: (ctx) => ctx.checkedEntries.length === 0,
        checkRunningEntries,
        checkRunningEntriesNotAvailable,
    },
    activities: {},
    services: {
        fetchDayEntries: (_, __) => ClockWorkModule.GetEntries(getFormattedDateString(_.displayDate)),
        deleteEntries: (_, __) => ClockWorkModule.DeleteEntries(_.checkedEntries),
        quickSuccess: (_, __) => new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve({ result: true });
            }, 50);
        }),
        quickFailure: (_, __) => new Promise((resolve, reject) => {
            setTimeout(() => {
                reject({ result: false });
            }, 50);
        }),
        delayedSuccess: (_, __) => new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve({ result: true });
            }, 2000);
        }),
        delayedFailure: (_, __) => new Promise((resolve, reject) => {
            setTimeout(() => {
                reject({ result: false });
            }, 2000);
        }),
    },
});
export default clockWorkMachine;
